import type {
  PointHubEvent,
  PointHubEventResult,
  PointHubInitEventConfig,
  PointHubScenarioEventConfig
} from '@ripple/point-hub-web-sdk';
import { getPointHubConfig } from './config';
import { MEETING_BOOKING_SESSION_RECOMMEND_EVENT_NAME, POINT_HUB_SDK_VERSION } from './constants';
import { emitPointHubDiagnostic } from './diagnostics';
import { getPointHubRuntimeCache } from './runtime-cache';
import { getOrCreateScenarioClient } from './sdk';
import type {
  NormalizedMeetingBookingSessionRecommendEvent,
  PointHubConfig,
  PointHubReportContext,
  PointHubReportResult
} from './types';
import { resolvePointHubUserInfo } from './user-context';
import { measurePointHubPayloadBytes } from './meeting-booking-session-recommend/payload';

export const findMeetingBookingScenario = (
  events: PointHubInitEventConfig[] = []
): PointHubScenarioEventConfig | null => {
  const scenario = events.find((event) => {
    return (
      event.event_kind === 'scenario' &&
      event.event_name === MEETING_BOOKING_SESSION_RECOMMEND_EVENT_NAME &&
      Number.isInteger(event.event_version) &&
      event.event_version > 0
    );
  });
  return scenario && scenario.event_kind === 'scenario' ? scenario : null;
};

export const ensurePointHubScenarioInitialized = async (
  forceRefresh = false,
  config: PointHubConfig = getPointHubConfig()
): Promise<PointHubScenarioEventConfig | null> => {
  const clientResult = getOrCreateScenarioClient(config);
  if (!clientResult.client) {
    if (clientResult.unavailableReason !== 'point_hub_disabled') {
      emitPointHubDiagnostic({
        code: clientResult.unavailableReason || 'scenario_client_unavailable',
        environment: config.environment
      });
    }
    return null;
  }

  const cache = getPointHubRuntimeCache();
  if (forceRefresh) cache.initPromise = null;
  if (cache.initPromise) return cache.initPromise;

  const initPromise = clientResult.client
    .init({
      clientType: 'web',
      sdkVersion: POINT_HUB_SDK_VERSION,
      environment: config.environment
    })
    .then((response) => {
      const scenario = findMeetingBookingScenario(response.events);
      if (!scenario) {
        emitPointHubDiagnostic({
          code: 'scenario_unavailable',
          environment: config.environment
        });
      }
      return scenario;
    })
    .catch((error: { code?: string; requestId?: string; status?: number }) => {
      if (cache.initPromise === initPromise) cache.initPromise = null;
      emitPointHubDiagnostic({
        code: 'scenario_init_failed',
        environment: config.environment,
        detail: {
          sdkCode: error && error.code,
          requestId: error && error.requestId,
          status: error && error.status
        }
      });
      return null;
    });

  cache.initPromise = initPromise;
  return initPromise;
};

export const initializePointHubAfterLogin = (config?: PointHubConfig): void => {
  const activeConfig = config || getPointHubConfig();
  ensurePointHubScenarioInitialized(false, activeConfig).catch(
    (error: { code?: string; requestId?: string; status?: number }) => {
      emitPointHubDiagnostic({
        code: 'scenario_preinit_failed',
        environment: activeConfig.environment,
        detail: {
          sdkCode: error && error.code,
          requestId: error && error.requestId,
          status: error && error.status
        }
      });
    }
  );
};

const toReportResult = (eventId: string, result: PointHubEventResult): PointHubReportResult => ({
  status: result.status,
  eventId: result.event_id || eventId,
  code: result.code,
  message: result.message
});

export const reportNormalizedMeetingBookingSessionRecommend = async (
  event: NormalizedMeetingBookingSessionRecommendEvent,
  context?: PointHubReportContext,
  config: PointHubConfig = getPointHubConfig()
): Promise<PointHubReportResult> => {
  const userInfo = resolvePointHubUserInfo(context);
  if (!userInfo) {
    emitPointHubDiagnostic({ code: 'invalid_user_context', eventId: event.eventId, environment: config.environment });
    return { status: 'skipped', eventId: event.eventId, code: 'invalid_user_context' };
  }
  if (event.properties.platform !== config.sourceType) {
    emitPointHubDiagnostic({
      code: 'source_type_mismatch',
      eventId: event.eventId,
      environment: config.environment
    });
    return { status: 'skipped', eventId: event.eventId, code: 'source_type_mismatch' };
  }

  const scenario = await ensurePointHubScenarioInitialized(false, config);
  if (!scenario) return { status: 'skipped', eventId: event.eventId, code: 'scenario_unavailable' };
  const client = getOrCreateScenarioClient(config).client;
  if (!client) return { status: 'skipped', eventId: event.eventId, code: 'scenario_client_unavailable' };

  const buildSdkEvent = (eventVersion: number): PointHubEvent => ({
    eventId: event.eventId,
    eventName: event.eventName,
    eventVersion,
    occurredAt: event.occurredAt,
    sourceType: config.sourceType,
    sourceName: config.sourceName,
    environment: config.environment,
    userInfo,
    sessionId: event.sessionId,
    actorId: userInfo.userId,
    subjectType: event.subjectType,
    subjectId: event.subjectId,
    action: event.action,
    result: event.result,
    durationMs: event.durationMs,
    properties: event.properties as unknown as Record<string, unknown>
  });

  try {
    let sdkEvent = buildSdkEvent(scenario.event_version);
    const payloadBytes = measurePointHubPayloadBytes(sdkEvent);
    if (payloadBytes === null) {
      emitPointHubDiagnostic({ code: 'payload_measure_failed', eventId: event.eventId, environment: config.environment });
      return { status: 'skipped', eventId: event.eventId, code: 'payload_measure_failed' };
    }
    emitPointHubDiagnostic({
      code: 'final_payload_bytes',
      eventId: event.eventId,
      environment: config.environment,
      bytes: payloadBytes
    });
    if (!config.maxPayloadBytes) {
      emitPointHubDiagnostic({ code: 'payload_limit_unavailable', eventId: event.eventId, environment: config.environment });
      return { status: 'skipped', eventId: event.eventId, code: 'payload_limit_unavailable' };
    }
    if (payloadBytes > config.maxPayloadBytes) {
      emitPointHubDiagnostic({
        code: 'payload_too_large',
        eventId: event.eventId,
        environment: config.environment,
        bytes: payloadBytes,
        detail: { maxPayloadBytes: config.maxPayloadBytes }
      });
      return { status: 'skipped', eventId: event.eventId, code: 'payload_too_large' };
    }

    let result = await client.track(sdkEvent);
    if (result.status === 'rejected' && result.code === 'unknown_event_schema') {
      const refreshedScenario = await ensurePointHubScenarioInitialized(true, config);
      if (refreshedScenario) {
        sdkEvent = buildSdkEvent(refreshedScenario.event_version);
        result = await client.track(sdkEvent);
      }
    }

    if (result.status === 'accepted_with_warning') {
      emitPointHubDiagnostic({
        code: 'track_accepted_with_warning',
        eventId: event.eventId,
        environment: config.environment,
        detail: { sdkCode: result.code }
      });
    } else if (result.status === 'rejected') {
      emitPointHubDiagnostic({
        code: 'track_rejected',
        eventId: event.eventId,
        environment: config.environment,
        detail: { sdkCode: result.code }
      });
    }
    return toReportResult(event.eventId, result);
  } catch (error) {
    const sdkError = error as { code?: string; requestId?: string; status?: number };
    emitPointHubDiagnostic({
      code: 'track_failed',
      eventId: event.eventId,
      environment: config.environment,
      detail: {
        sdkCode: sdkError && sdkError.code,
        requestId: sdkError && sdkError.requestId,
        status: sdkError && sdkError.status
      }
    });
    return { status: 'rejected', eventId: event.eventId, code: sdkError && sdkError.code };
  }
};
